import React from 'react';
import { flushSync } from 'react-dom';
import styled, { css } from 'styled-components';
import PubSub from 'pubsub-js';
import { connect } from 'react-redux';
import { StatusQuestionMark } from '../../assets/icons';
import LoadingSpinner from '../loading-spinner';
import { Button } from '../button';
import { useTheme } from '../../themes';
import ConnectedKeyboard from '../keyboard/connected-keyboard';
import { getLogger } from '../../lib/logger-plus';
import { Block, Heading, Text } from '../typography';
import { setModalDisplayed } from '../../store/reducers/application/application.actions';

export * from './content';

const logger = getLogger('modal');

export type ModalType = 'success' | 'error' | 'info' | 'warning' | 'default' | 'expired';

export interface IAction {
  className?: string;
  label: string;
  exit?: boolean;
  type?: 'primary' | 'secondary' | 'primaryRound' | 'secondaryRound';
  cb?(): void;
  width?: string;
  button?: JSX.Element;
  disabled?: boolean;
}

export interface ITimedModalProps {
  startTimer(modalState: IModalState): void;
  stopTimer(): void;
  hasTimerStarted(): boolean;
  checkTimeOut(): void;
}

export interface IModalState {
  actions: IAction[];
  actionAlign?: string;
  authoriser?: boolean;
  autowidth?: boolean;
  className?: string;
  children?: React.ReactNode;
  content?: React.ReactNode;
  contentAlign?: 'center' | 'left' | 'right';
  context?: React.ReactNode;
  contextWidth?: string;
  disableButtonsOnAction?: boolean;
  disableButtons?: boolean;
  hasFooter?: boolean;
  includeContentPadding?: boolean;
  open: boolean;
  override?: boolean;
  ref?: React.RefObject<HTMLDivElement> | null;
  subservient?: boolean;
  title?: React.ReactNode;
  leading?: string;
  description?: string;
  type?: ModalType;
  timedModalProps?: ITimedModalProps;
  width?: string;
  onBackgroundClick?: () => void;
  enableKeyboard?: boolean;
  emailModeKeyboard?: boolean;
  brandBackground?: boolean;
  alternateModalBackgroundColour?: boolean;
  noteAcceptorState?: React.ReactNode;
}

interface ICustomModal {
  title: any;
  leading?: string;
  description?: string;
  context?: any;
  content?: any;
  actions?: IAction[];
  actionAlign?: string;
  override?: boolean;
  subservient?: boolean;
  width?: string;
  ref?: React.RefObject<HTMLDivElement>;
  disableButtonsOnAction?: boolean;
  alternateModalBackgroundColour?: boolean;
}

export const defaultState: IModalState = {
  actions: [],
  actionAlign: 'center',
  authoriser: false,
  autowidth: false,
  className: '',
  children: undefined,
  content: '',
  contentAlign: 'center',
  context: '',
  contextWidth: '45rem',
  disableButtonsOnAction: false,
  disableButtons: false,
  hasFooter: false,
  includeContentPadding: true,
  open: false,
  override: false,
  subservient: true,
  title: '',
  leading: '',
  description: '',
  type: 'default',
  width: 'standard',
  onBackgroundClick: () => {},
  enableKeyboard: false,
  emailModeKeyboard: false,
  brandBackground: false,
  alternateModalBackgroundColour: false,
  noteAcceptorState: ''
};

export const defaultCloseButton = {
  className: 'qa-modal-close',
  exit: true,
  label: 'Close',
  width: '14rem'
};

export const initModal = (modalState: IModalState) => {
  logger.info({ title: modalState.title }, 'initModal');
  PubSub.publish('show-modal', modalState);
};

export const closeModal = ({ title }: { title?: string } = {}) => {
  logger.info({ title }, 'closeModal');
  if (title) PubSub.publish('close-modal', title);
  else initModal(defaultState);
};

export const showProcessingModal = (title: string = 'Processing request') =>
  initModal({
    actions: [],
    autowidth: false,
    content: (
      <React.Fragment>
        <LoadingSpinner />
      </React.Fragment>
    ),
    context: <span>Please wait...</span>,
    open: true,
    title,
    width: 'default'
  });

export const showReferralModal = (spec: ICustomModal) => {
  const { override = false, subservient = true } = spec;
  initModal({
    actions: spec.actions || [defaultCloseButton],
    actionAlign: 'right',
    autowidth: false,
    content: spec.context,
    disableButtonsOnAction: true,
    open: true,
    override,
    subservient,
    title: spec.title,
    width: 'large'
  });
};

export const showSuccessModal = (spec: ICustomModal) => {
  const { override = false, subservient = true, disableButtonsOnAction = false } = spec;
  initModal({
    actions: spec.actions || [defaultCloseButton],
    actionAlign: 'center',
    autowidth: false,
    leading: spec.leading,
    description: spec.description,
    context: spec.context,
    content: spec.content,
    open: true,
    override,
    subservient,
    disableButtonsOnAction,
    title: spec.title,
    type: 'success',
    width: 'default',
    alternateModalBackgroundColour: spec.alternateModalBackgroundColour
  });
};

export const showFailureModal = (spec: ICustomModal) => {
  const { override = false, subservient = true } = spec;
  initModal({
    actions: spec.actions || [defaultCloseButton],
    actionAlign: 'center',
    leading: spec.leading,
    description: spec.description,
    content: spec.content,
    context: spec.context,
    open: true,
    override,
    subservient,
    title: spec.title,
    type: 'error',
    width: spec.width || 'default'
  });
};

export const showInfoModal = (spec: ICustomModal) =>
  initModal({
    actions: spec.actions || [defaultCloseButton],
    leading: spec.leading,
    description: spec.description,
    content: <React.Fragment>{spec.content}</React.Fragment>,
    context: spec.context,
    open: true,
    title: spec.title,
    width: spec.width || 'default'
  });

export const showWarningModal = (spec: ICustomModal) => {
  const { override = false, subservient = true } = spec;
  initModal({
    ref: spec.ref,
    actions: spec.actions || [defaultCloseButton],
    leading: spec.leading,
    description: spec.description,
    content: spec.content,
    context: spec.context,
    open: true,
    override,
    subservient,
    title: spec.title,
    type: 'warning',
    width: spec.width || 'default'
  });
};

export const showNotFoundModal = (spec: ICustomModal) =>
  initModal({
    actionAlign: 'center',
    actions: [defaultCloseButton],
    content: <NotFoundContent />,
    context: spec.context,
    open: true,
    title: spec.title,
    width: spec.width || 'default'
  });

const NotFoundContent = () => {
  const theme = useTheme();
  return <StatusQuestionMark secondaryFill={theme.colors.grey300} />;
};

export const showConfirmModal = (title: React.ReactNode, context: React.ReactNode, onConfirm: () => void) =>
  initModal({
    open: true,
    width: 'default',
    type: 'warning',
    title,
    content: <ConfirmModalContent context={context} />,
    context: null,
    actions: [
      { label: 'Cancel', exit: true, className: 'qa-confirm-modal-cancel' },
      { label: 'Confirm', type: 'primary', className: 'qa-confirm-modal-confirm', cb: onConfirm }
    ]
  });

const ConfirmModalContent = ({ context }: { context: React.ReactNode }) => {
  return <ConfirmContext weight="bold">{context}</ConfirmContext>;
};

const actionButtonsOrder = {
  primary: 0,
  secondary: 1,
  alert: 2
};

const byActionButtonType = (actionA: IAction, actionB: IAction) =>
  actionButtonsOrder[actionB.type || 'secondary'] - actionButtonsOrder[actionA.type || 'secondary'];

export class UnconnectedModal extends React.Component<
  { spec?: IModalState; setModalDisplayed: (modalDisplayed: boolean) => void },
  IModalState
> {
  constructor(props: any) {
    super(props);

    this.state = {
      ...defaultState,
      ...this.props.spec,
      disableButtons: false
    };
  }

  close = () => {
    this.setState(defaultState);
  };

  componentDidMount() {
    PubSub.subscribe('close-modal', this.closeModal);
    PubSub.subscribe('show-modal', this.showModal);
  }

  componentWillUnmount() {
    const { timedModalProps } = this.state;
    timedModalProps && timedModalProps.hasTimerStarted() && timedModalProps.stopTimer();
    PubSub.unsubscribe(this.closeModal);
    PubSub.unsubscribe(this.showModal);
  }

  shouldComponentUpdate() {
    const { subservient = true } = this.state;
    return subservient;
  }

  componentDidUpdate(
    prevProps: { spec?: IModalState; setModalDisplayed: (modalDisplayed: boolean) => void },
    prevState: IModalState
  ) {
    if (prevState.open !== this.state.open) {
      this.props.setModalDisplayed(this.state.open);
      PubSub.publish('modal-state-changed', { ...this.state });
    } else if (this.state.open && prevState.title !== this.state.title) {
      PubSub.publish('modal-state-changed', { ...this.state });
    }
  }

  closeModal = (type: any, title: string) => {
    logger.info({ currentModalTitle: this.state.title, requestTitle: title }, 'close-modal');
    if (this.state.title === title) {
      this.setState({ subservient: true }, () => {
        this.showModal(type, defaultState);
      });
    } else {
      logger.warn('close-modal: ignoring request because title is different');
    }
  };

  showModal = (type: any, payload: IModalState) => {
    logger.info({ currentModalTitle: this.state.title, requestTitle: payload.title }, 'show-modal');
    if (!this.state.subservient && this.state.open) {
      if (payload.open === false) {
        logger.info('show-modal: forcing state to "closed"');
        flushSync(() => this.setState({ subservient: true, open: false }));
        return;
      } else {
        if (payload.override) {
          logger.info('show-modal: override requested');
          flushSync(() => this.setState({ subservient: true, open: true, override: false }));
        } else {
          return;
        }
      }
    }

    const { timedModalProps: payloadTimedModalProps } = payload;
    const { timedModalProps: stateTimedModalProps } = this.state;

    if (stateTimedModalProps && !payloadTimedModalProps && stateTimedModalProps.hasTimerStarted()) {
      stateTimedModalProps.stopTimer();
    }

    const buttonsDisabled = payload.title === this.state.title ? this.state.disableButtons : false;

    flushSync(() => this.setState({ ...defaultState, ...payload, disableButtons: buttonsDisabled }));
    logger.info({ currentModalTitle: this.state.title }, 'show-modal: state update');

    if (payloadTimedModalProps) {
      payloadTimedModalProps.hasTimerStarted()
        ? payloadTimedModalProps.checkTimeOut()
        : payloadTimedModalProps.startTimer(payload);
    }
  };

  executeActionCallack = (action: IAction) => {
    if (action && action.cb) {
      action.cb();
    }
  };

  handleActionButtonClick = (action: IAction) => {
    const { disableButtonsOnAction } = this.state;
    if (action.exit) {
      this.setState({ subservient: true }, () => {
        this.executeActionCallack(action);
        this.close();
      });
    } else if (action.cb) {
      this.setState({ subservient: true, disableButtons: disableButtonsOnAction }, () =>
        this.executeActionCallack(action)
      );
    }
  };

  renderSingleAction = (action: IAction) => {
    const { disableButtons } = this.state;
    if (action.button) {
      return <ActionItem key={action.label}>{action.button}</ActionItem>;
    }
    return (
      <ActionItem key={action.label}>
        <Button
          autoFocus={false}
          className={`qa-modal-button ${action.className}`}
          width={action.width}
          disabled={disableButtons || action.disabled}
          modifiers={action.type}
          onClick={() => this.handleActionButtonClick(action)}>
          <span>{action.label}</span>
        </Button>
      </ActionItem>
    );
  };

  renderActionButtons = () => {
    const modalModifiers: Record<string, 'primaryRound' | 'secondaryRound'> = {
      primary: 'primaryRound',
      primaryRound: 'primaryRound',
      secondary: 'secondaryRound',
      secondaryRound: 'secondaryRound'
    };
    const actions: IAction[] = [...this.state.actions].sort(byActionButtonType).map(action => ({
      ...action,
      type: modalModifiers[action.type || 'secondary'] || 'secondaryRound'
    }));

    return (
      <ActionList actionAlign={this.state.actionAlign || 'center'}>{actions.map(this.renderSingleAction)}</ActionList>
    );
  };

  render() {
    const {
      autowidth,
      className,
      title,
      leading,
      description,
      type,
      children,
      content,
      context,
      contextWidth,
      actions,
      open,
      ref,
      width,
      onBackgroundClick,
      hasFooter = false,
      includeContentPadding = true,
      enableKeyboard,
      emailModeKeyboard = false,
      brandBackground,
      noteAcceptorState,
      alternateModalBackgroundColour,
      contentAlign
    } = this.state;

    const showFooter = actions.length > 0 || (!!context && context !== '');

    if (!open) {
      return null;
    }
    return (
      <ModalOverlay
        className="qa-modal-overlay"
        ref={ref}
        onClick={onBackgroundClick}
        brandBackground={brandBackground}>
        <FlexGrowDiv width={width}>
          <ModalWrapper
            autowidth={autowidth}
            className={`qa-modal-wrapper ${className || ''}`}
            width={width}
            alternateModalBackgroundColour={alternateModalBackgroundColour}>
            {children}
            {title && (
              <ModalHeader className="qa-modal-header" type={type}>
                <ModalTitle as="h3" size="xl" weight="bold" className="qa-modal-title" type={type}>
                  <span>{title}</span>
                </ModalTitle>
              </ModalHeader>
            )}
            {leading && (
              <Leading as="p" size="lg" lineHeight="2.4rem" weight="medium">
                {leading}
              </Leading>
            )}
            {description && <Block lineHeight="2.4rem">{description}</Block>}
            {content && (
              <ModalContent
                autowidth={autowidth}
                className="qa-modal-content"
                withFooter={hasFooter || showFooter}
                includePadding={includeContentPadding}
                contentAlign={contentAlign}>
                {content}
              </ModalContent>
            )}
            {showFooter && (
              <ModalFooter className="qa-modal-footer">
                {context && (
                  <ActionContext className="qa-modal-context" width={contextWidth}>
                    {context}
                  </ActionContext>
                )}
                {this.renderActionButtons()}
              </ModalFooter>
            )}
          </ModalWrapper>
        </FlexGrowDiv>
        {noteAcceptorState}
        {enableKeyboard && <ConnectedKeyboard keyboardParentRef={ref} emailMode={emailModeKeyboard} />}
      </ModalOverlay>
    );
  }
}

const mapDispatchToProps = {
  setModalDisplayed
};

export const Modal = connect(null, mapDispatchToProps)(UnconnectedModal);

const modalFunctions = {
  closeModal,
  defaultCloseButton,
  initModal,
  Modal,
  showFailureModal,
  showWarningModal,
  showNotFoundModal,
  showProcessingModal,
  showReferralModal,
  showSuccessModal,
  showInfoModal
};

export default modalFunctions;

export function ModalContainer({
  children,
  ref
}: {
  children: React.ReactElement;
  ref?: React.RefObject<HTMLDivElement> | null;
}) {
  return (
    <ModalOverlay {...{ ref }}>
      <FlexGrowDiv>
        <ModalWrapper>{children}</ModalWrapper>
      </FlexGrowDiv>
    </ModalOverlay>
  );
}

export const ModalOverlay = styled.div<{ brandBackground?: boolean; zIndex?: number }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: absolute;
  height: 100vh;
  top: 0;
  background: ${({ theme, brandBackground }) =>
    brandBackground ? theme.components.header.background : theme.components.global.overlay};
  left: 0;
  z-index: ${({ theme, zIndex }) => zIndex || theme.layers.modals};
  width: 100vw;
`;

export const FlexGrowDiv = styled.div<{ width?: string }>`
  ${({ width }) =>
    width === 'full-page' &&
    `
      width: calc(100% - 10rem);
      margin: 0 5rem;
    `};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
export const ModalWrapper = styled.aside<{
  autowidth?: boolean;
  width?: string;
  alternateModalBackgroundColour?: boolean;
}>`
  margin: auto;
  ${({ autowidth }) =>
    autowidth &&
    `
      width: auto;
    `};
  ${({ width }) =>
    width === 'default' &&
    `
      width: 68.5rem;
    `};
  ${({ width }) =>
    width === 'large' &&
    `
      width: 94rem;
    `};
  ${({ width }) =>
    width === 'extra-large' &&
    `
    margin: 0 25rem;
    width: calc(100% - 50rem);
    `};
  ${({ width }) =>
    width === 'full-page' &&
    `
      margin: 0 5rem;
      width: calc(100% - 10rem);
    `};
  background: ${({ theme, alternateModalBackgroundColour }) =>
    alternateModalBackgroundColour ? theme.colors.status.alert : theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  text-align: center;
`;

export type BackgroundTypes = 'default' | 'success' | 'error' | 'expired' | 'warning' | (string & {});

const headerBackground = ({ theme, type = 'default' }) => {
  if (type === 'success') return theme.colors.modals.success;
  if (type === 'error') return theme.colors.modals.error;
  if (type === 'expired') return theme.colors.modals.expired;
  if (type === 'warning') return theme.colors.modals.warning;
  return theme.colors.modals.neutral;
};

const headerColor = ({ theme, type = 'default' }) => {
  if (type === 'error' || type === 'expired') return theme.colors.white;
  return theme.colors.brand.foreground;
};

export const ModalHeader = styled.header<{ type?: BackgroundTypes }>`
  line-height: 67px;
  border-radius: 4px 4px 0 0;
  background-color: ${headerBackground};
  text-align: center;
`;

export const ModalTitle = styled(Heading)<{ type?: BackgroundTypes }>`
  color: ${headerColor};
  margin: 0;
`;

export const Leading = styled(Text)`
  margin-top: 2.7rem;
`;

export const ModalContent = styled.div<{
  autowidth?: boolean;
  withFooter: boolean;
  includePadding?: boolean;
  contentAlign?: 'center' | 'left' | 'right';
}>`
  align-items: ${({ contentAlign }) =>
    contentAlign === 'left' ? 'flex-start' : contentAlign === 'right' ? 'flex-end' : 'center'};
  display: flex;
  flex-direction: column;
  ${({ autowidth }) => css`
    margin: 0 auto;
    min-width: ${autowidth ? 'none' : '600px'};
    position: relative;
  `};
  ${({ includePadding = true, withFooter }) =>
    includePadding && `padding: 1.2rem 3.8rem ${withFooter ? '0' : '3rem'};`};
`;

export const ModalFooter = styled.footer`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2.4rem 3.75rem;
`;

export const ActionContext = styled.div<{ width?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin: 0 auto;
  width: ${({ width = '45rem' }) => width};
  white-space: pre-wrap;
`;

export const ActionList = styled.ul<{ actionAlign?: string }>`
  display: flex;
  justify-content: center;
  ${({ actionAlign }) =>
    actionAlign === 'right' &&
    css`
      justify-content: flex-end;
    `}
  ${({ actionAlign }) =>
    actionAlign === 'left' &&
    css`
      justify-content: flex-start;
    `}
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const ActionItem = styled.li`
  &:not(:first-child) {
    margin-left: 1.3rem;
  }
`;

const ConfirmContext = styled(Block)`
  margin: 1.6rem 0 0;
`;

export { ModalContextMessage } from './modal-context-message';
